import React, { useState } from 'react';
import { Plus, Trash2, AlertCircle, MessageSquare, Box, Copy, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSonas } from '../SonasContext';
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const ConversationSidebar = () => {
  const { 
    conversations, 
    activeConversationId, 
    setActiveConversationId, 
    setIsCreateDialogOpen,
    setIsPossessionsOpen,
    deleteProject,
    isLoading
  } = useSonas();
  
  const navigate = useNavigate();
  const [projectToDelete, setProjectToDelete] = useState(null);

  const sortedConversations = Object.values(conversations ?? {}).sort((a, b) => b.id.localeCompare(a.id));

  const handleSelect = (id) => {
    setActiveConversationId(id);
    navigate(`/app/chat/${id}`);
  };

  const handleCopyId = (e, conv) => {
    e.stopPropagation();
    const idToCopy = conv.share_id || conv.id;
    navigator.clipboard.writeText(idToCopy);
    toast.success("Project ID copied", { duration: 1500 });
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setProjectToDelete(id);
  };

  const confirmDelete = async () => {
    if (projectToDelete) {
      const success = await deleteProject(projectToDelete);
      if (success && activeConversationId === projectToDelete) {
        navigate('/app');
      }
      setProjectToDelete(null);
    }
  };

  return (
    <>
      <aside className="w-[260px] h-full flex flex-col bg-sonas-bg border-r border-white/[0.04]">
        <div className="p-5 space-y-3">
          <Button 
            onClick={() => setIsCreateDialogOpen(true)}
            className="w-full h-10 bg-white text-black hover:bg-white/90 rounded-xl justify-start gap-3 text-[13px] font-bold transition-all shadow-md"
          >
            <Plus className="h-4 w-4" /> New Project
          </Button>
          
          <Button 
            onClick={() => setIsPossessionsOpen(true)}
            variant="ghost"
            className="w-full h-10 bg-sonas-accent/5 hover:bg-sonas-accent/10 text-sonas-accent rounded-xl justify-start gap-3 text-[13px] font-bold transition-all border border-sonas-accent/10"
          >
            <Box className="h-4 w-4" /> My Possessions
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 space-y-0.5 sonas-scrollbar">
          <div className="px-3 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-sonas-dim">Workspaces</div>
          
          {isLoading ? (
            <div className="space-y-2 px-1">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-12 w-full rounded-xl bg-white/[0.02]" />
              ))}
            </div>
          ) : (
            sortedConversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => handleSelect(conv.id)}
                className={cn(
                  "w-full flex flex-row items-center justify-between px-3 py-3 rounded-xl transition-all text-left group border border-transparent mb-1",
                  activeConversationId === conv.id 
                    ? "bg-white/[0.03] border-white/[0.05] shadow-sm" 
                    : "hover:bg-white/[0.01]"
                )}
              >
                <div className="flex flex-col items-start truncate mr-2">
                  <span className={cn(
                    "text-[12.5px] font-semibold truncate w-full",
                    activeConversationId === conv.id ? "text-sonas-accent" : "text-sonas-text"
                  )}>
                    {conv.name}
                  </span>
                  <span className="text-[9px] font-mono text-sonas-dim mt-0.5">{conv.date}</span>
                </div>
                
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleCopyId(e, conv)}
                    className="h-7 w-7 hover:bg-sonas-accent/10 hover:text-sonas-accent rounded-lg shrink-0"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleDeleteClick(e, conv.id)}
                    className="h-7 w-7 hover:bg-red-500/10 hover:text-red-500 rounded-lg shrink-0"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </button>
            ))
          )}
        </div>

        <div className="p-4">
          <a 
            href="https://discord.gg/S25DbuEVSy" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex flex-col gap-2.5 p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05] hover:border-sonas-accent/30 transition-all group"
          >
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-sonas-accent/10 flex items-center justify-center text-sonas-accent">
                <MessageCircle size={15} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest">Discord Server</span>
            </div>
            <p className="text-[10px] leading-relaxed text-sonas-dim group-hover:text-sonas-muted transition-colors">
              Access exclusive <span className="text-sonas-accent font-bold">gifts</span> and system alerts.
            </p>
          </a>
        </div>
      </aside>

      <AlertDialog open={!!projectToDelete} onOpenChange={(open) => !open && setProjectToDelete(null)}>
        <AlertDialogContent className="glass-surface max-w-sm rounded-[32px] p-8 border-none ring-0">
          <AlertDialogHeader className="space-y-4">
            <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-2">
              <AlertCircle className="h-6 w-6 text-red-500" />
            </div>
            <AlertDialogTitle className="text-2xl font-syne text-center">Delete Project?</AlertDialogTitle>
            <AlertDialogDescription className="text-center text-sm text-sonas-muted leading-relaxed">
              This will permanently erase all messages and scripts associated with this project.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:flex-col gap-3 mt-6">
            <AlertDialogAction 
              onClick={confirmDelete}
              className="w-full bg-red-500 hover:bg-red-600 text-white font-bold rounded-2xl h-12 shadow-lg shadow-red-500/20 border-none transition-all active:scale-[0.98]"
            >
              Delete
            </AlertDialogAction>
            <AlertDialogCancel className="w-full rounded-2xl bg-sonas-surface2/50 border-none h-12 text-sonas-text font-semibold hover:bg-sonas-surface2 transition-all">
              Abort
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ConversationSidebar;